import { GK_CHEMSTYLE_ID_START, } from "./chemStyleAccelerate";
/**
 * How EA weighs in-game stats into each face-card stat, in `player.attributes`
 * index order (PAC, SHO, PAS, DRI, DEF, PHY / DIV, HAN, KIC, REF, SPD, POS).
 *
 * Arrays rather than a code-keyed object so the order is explicit.
 */
const OUTFIELD_WEIGHTS = [
    { acceleration: 0.45, sprintSpeed: 0.55 },
    {
        positioning: 0.05,
        finishing: 0.45,
        shotPower: 0.2,
        longShots: 0.2,
        volleys: 0.05,
        penalties: 0.05,
    },
    {
        vision: 0.2,
        crossing: 0.2,
        freeKickAccuracy: 0.05,
        shortPassing: 0.35,
        longPassing: 0.15,
        curve: 0.05,
    },
    {
        agility: 0.1,
        balance: 0.05,
        reactions: 0.05,
        ballControl: 0.3,
        dribbling: 0.45,
        composure: 0.05,
    },
    {
        interceptions: 0.2,
        headingAccuracy: 0.1,
        defensiveAwareness: 0.3,
        standingTackles: 0.3,
        slidingTackles: 0.1,
    },
    { jumping: 0.05, stamina: 0.25, strength: 0.5, aggression: 0.2 },
];
const KEEPER_WEIGHTS = [
    { gkDiving: 1 },
    { gkHandling: 1 },
    { gkKicking: 1 },
    { gkReflexes: 1 },
    { acceleration: 0.6, sprintSpeed: 0.4 },
    { gkPositioning: 1 },
];
const NO_BONUS = {
    allStatsBonus: [],
    specificStatsBonus: {},
};
/**
 * What a chemstyle adds to a player's stats at a chemistry level: the raw
 * per-stat bonuses, and what those add up to on each of the six face stats.
 *
 * The face-stat bonus is a re-weighting rather than a sum — EA caps each
 * in-game stat at 99 before weighing it, so a boost onto a 97 is worth less
 * than its face value. Hence base stats in and a delta out.
 */
export const calculatePlayerStatsBonus = ({ chemStyleId, chemistry, isGk, inGameAttributes, faceCardStats, chemstyleBonuses, }) => {
    // No chemistry means no bonuses, and a style the player can't wear gives
    // them nothing — a keeper can't take an outfield style or the reverse.
    if (chemistry === 0)
        return NO_BONUS;
    if (isGk !== chemStyleId >= GK_CHEMSTYLE_ID_START)
        return NO_BONUS;
    // Still loading: render the base card rather than nothing.
    const bonuses = chemstyleBonuses?.[`${chemStyleId}`]?.[`${chemistry}`];
    if (!bonuses || !inGameAttributes || !faceCardStats)
        return NO_BONUS;
    const allStatsBonus = (isGk ? KEEPER_WEIGHTS : OUTFIELD_WEIGHTS).map((weights, index) => {
        const entries = Object.entries(weights);
        // A stat we don't have is not a stat of zero: without all of them the
        // weighted total is meaningless, so report no change and show the base.
        if (entries.some(([key]) => inGameAttributes[key] == null))
            return 0;
        // A style that feeds none of these stats leaves the category alone. The
        // weighted total is a few tenths off the face stat EA prints, so without
        // this the untouched categories drift by a point (as they do on web).
        if (!entries.some(([key]) => bonuses[key]))
            return 0;
        const boosted = entries.reduce((total, [key, weight]) => total +
            Math.min(99, (bonuses[key] || 0) + inGameAttributes[key]) * weight, 0);
        return Math.round(boosted - (faceCardStats[index] ?? 0));
    });
    return { allStatsBonus, specificStatsBonus: bonuses };
};
/** A stat with its chemstyle bonus applied, capped at EA's 99. */
export const withBonus = (value, bonus) => Math.min(99, value + bonus);
//# sourceMappingURL=chemStyleBonuses.js.map